import { useEffect, useRef, useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { PrintOutlined } from "@mui/icons-material";
import { cashiLogo } from "../../include/cashiBrand";
import { consoleColors, headingCase, primaryButtonSx } from "../admin/consoleTheme";

const PREVIEW = 0.42;
const FONTS = "https://fonts.googleapis.com/css2?family=Bungee&family=Fredoka:wght@600;800&family=Luckiest+Guy&family=Manrope:wght@600;800&family=Pacifico&display=swap";
const PRIZE_COLORS = ["#21a7ec", "#f59e0b", "#16a34a", "#ef4444", "#7c3aed", "#ec4899", "#0d9488", "#f97316"];

function absoluteUrl(src) {
  if (!src) return "";
  try { return new URL(src, window.location.href).href; } catch { return src; }
}

function loadPosterFonts() {
  if (document.getElementById("spin-poster-fonts")) return;
  const link = document.createElement("link");
  link.id = "spin-poster-fonts";
  link.rel = "stylesheet";
  link.href = FONTS;
  document.head.appendChild(link);
}

function Spark({ top, left, size, color, rotate }) {
  return (
    <div style={{ position: "absolute", top, left, width: size, height: size, color, transform: `rotate(${rotate || 0}deg)`, zIndex: 2 }}>
      <svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor">
        <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" />
      </svg>
    </div>
  );
}

function Gift({ top, right, size }) {
  return (
    <div style={{ position: "absolute", top, right, width: size, height: size, zIndex: 2 }}>
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        <rect x="10" y="26" width="44" height="30" rx="4" fill="#fbbf24" />
        <rect x="10" y="26" width="44" height="10" fill="#f59e0b" />
        <rect x="28" y="26" width="8" height="30" fill="#ef4444" />
        <path d="M32 26c-8-10-18-8-18-2 0 6 10 8 18 2z" fill="#ef4444" />
        <path d="M32 26c8-10 18-8 18-2 0 6-10 8-18 2z" fill="#ef4444" />
      </svg>
    </div>
  );
}

function Trophy({ bottom, left, size }) {
  return (
    <div style={{ position: "absolute", bottom, left, width: size, height: size, zIndex: 2 }}>
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        <path d="M16 16h32v8c0 10-7 18-16 18S16 34 16 24v-8z" fill="#fbbf24" />
        <path d="M16 18c-6 0-10 4-10 10 0 6 6 8 10 6" fill="none" stroke="#f59e0b" strokeWidth="5" />
        <path d="M48 18c6 0 10 4 10 10 0 6-6 8-10 6" fill="none" stroke="#f59e0b" strokeWidth="5" />
        <rect x="28" y="42" width="8" height="8" fill="#f59e0b" />
        <rect x="20" y="50" width="24" height="6" rx="2" fill="#fde68a" />
      </svg>
    </div>
  );
}

function polar(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function slicePath(cx, cy, r, startDeg, endDeg) {
  const [x1, y1] = polar(cx, cy, r, startDeg);
  const [x2, y2] = polar(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

function PrizeWheel({ prizes }) {
  const items = prizes.length ? prizes : ["Prize"];
  const count = items.length;
  const size = 560;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 248;
  const sweep = 360 / count;
  const fontSize = count > 6 ? 18 : count > 4 ? 22 : 26;
  const labelLen = count > 6 ? 10 : 14;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="148mm" height="148mm" style={{ display: "block", filter: "drop-shadow(0 10px 18px rgba(0,0,0,0.28))" }}>
      <circle cx={cx} cy={cy} r={radius + 14} fill="#fff" />
      {items.map((title, index) => {
        const start = index * sweep - 90;
        const end = start + sweep;
        const mid = start + sweep / 2;
        const [tx, ty] = polar(cx, cy, radius * 0.62, mid);
        return (
          <g key={`${title}-${index}`}>
            <path d={slicePath(cx, cy, radius, start, end)} fill={PRIZE_COLORS[index % PRIZE_COLORS.length]} stroke="#ffffff" strokeWidth="6" />
            <text
              x={tx}
              y={ty}
              fill="#ffffff"
              fontSize={fontSize}
              fontWeight="800"
              fontFamily="Fredoka, Manrope, sans-serif"
              textAnchor="middle"
              dominantBaseline="middle"
              transform={`rotate(${mid}, ${tx}, ${ty})`}
            >
              {String(title).slice(0, labelLen)}
            </text>
          </g>
        );
      })}
      <circle cx={cx} cy={cy} r={48} fill="#111111" stroke="#ffffff" strokeWidth="8" />
      <text x={cx} y={cy + 6} fill="#fde047" fontSize="22" fontWeight="800" fontFamily="Fredoka, sans-serif" textAnchor="middle">SPIN</text>
      <polygon points={`${cx - 18},18 ${cx + 18},18 ${cx},52`} fill="#111111" stroke="#ffffff" strokeWidth="4" />
    </svg>
  );
}

export default function SpinPoster({ wheel, storeName, storeImage }) {
  const sheetRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("SPIN");
  const [winWord, setWinWord] = useState("WIN");
  const [description, setDescription] = useState("Walk in, spin the wheel on our screen, and win a prize.");
  const [askText, setAskText] = useState("Ask us to start your spin");
  const [photoOk, setPhotoOk] = useState(Boolean(storeImage));

  const logoSrc = absoluteUrl(cashiLogo);
  const name = headingCase(storeName) || "Your Store";
  const prizes = (wheel?.options || []).map((item) => item.title).filter(Boolean);

  useEffect(() => { loadPosterFonts(); }, []);

  const openSheet = () => {
    setTitle("SPIN");
    setWinWord("WIN");
    setDescription("Walk in, spin the wheel on our screen, and win a prize.");
    setAskText("Ask us to start your spin");
    setPhotoOk(Boolean(storeImage));
    setOpen(true);
  };

  const handlePrint = () => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>${name} Spin & Win</title>
      <link href="${FONTS}" rel="stylesheet" />
      <style>
        @page { size: A4 portrait; margin: 0; }
        html, body { margin: 0; padding: 0; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      </style></head><body>${sheet.outerHTML}</body></html>`);
    doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 350);
    };
  };

  const sheetStyle = {
    width: "210mm",
    height: "297mm",
    boxSizing: "border-box",
    position: "relative",
    overflow: "hidden",
    color: "#fff",
    fontFamily: '"Manrope", Helvetica, Arial, sans-serif',
    background: "linear-gradient(160deg, #1d4ed8 0%, #7c3aed 38%, #db2777 72%, #f59e0b 100%)",
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
  };

  return (
    <>
      <Button size="small" sx={{ textTransform: "none" }} onClick={openSheet} startIcon={<PrintOutlined sx={{ fontSize: 16 }} />}>Print poster</Button>
      {open && (
        <Box sx={{ position: "fixed", inset: 0, zIndex: 1400, background: "rgba(10,10,10,0.55)", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          <Box sx={{ width: "min(1040px, 100%)", maxHeight: "94vh", overflow: "auto", background: "#fff", borderRadius: 3, p: { xs: 2, md: 2.5 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1 }}>
              <Box sx={{ fontSize: 18, fontWeight: 750, color: consoleColors.ink }}>A4 Spin & Win poster</Box>
              <Button sx={{ minWidth: 36, color: consoleColors.muted }} onClick={() => setOpen(false)} aria-label="Close">✕</Button>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "auto 300px" }, gap: 2.5, alignItems: "start" }}>
              <Box sx={{ width: `calc(210mm * ${PREVIEW})`, height: `calc(297mm * ${PREVIEW})`, overflow: "hidden", mx: "auto", boxShadow: "0 16px 40px rgba(15,23,42,0.16)", borderRadius: 1 }}>
                <Box sx={{ transform: `scale(${PREVIEW})`, transformOrigin: "top left" }}>
                  <div ref={sheetRef} style={sheetStyle}>
                    <div style={{ position: "absolute", width: "110mm", height: "110mm", borderRadius: "50%", background: "rgba(255,255,255,0.12)", top: "-38mm", right: "-36mm" }} />
                    <div style={{ position: "absolute", width: "86mm", height: "86mm", borderRadius: "50%", background: "rgba(250,204,21,0.22)", bottom: "42mm", left: "-40mm" }} />
                    <div style={{ position: "absolute", width: "48mm", height: "48mm", borderRadius: "50%", background: "rgba(236,72,153,0.28)", top: "86mm", right: "-16mm" }} />
                    <Spark top="12mm" left="12mm" size="10mm" color="#fde68a" />
                    <Spark top="28mm" left="178mm" size="8mm" color="#fff" rotate={20} />
                    <Spark top="118mm" left="8mm" size="7mm" color="#f9a8d4" rotate={30} />
                    <Spark top="248mm" left="186mm" size="9mm" color="#facc15" rotate={12} />
                    <Gift top="14mm" right="10mm" size="16mm" />
                    <Trophy bottom="30mm" left="8mm" size="18mm" />

                    <div style={{ position: "relative", zIndex: 3, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", padding: "9mm 10mm 26mm", boxSizing: "border-box" }}>
                      <img src={logoSrc} alt="Cashi" style={{ height: "10mm", width: "auto" }} />
                      {storeImage && photoOk && (
                        <img src={storeImage} alt="" onError={() => setPhotoOk(false)} style={{ width: "16mm", height: "16mm", objectFit: "cover", borderRadius: "50%", border: "2px solid #fff", marginTop: "3mm" }} />
                      )}
                      <div style={{ marginTop: "2.5mm", fontFamily: '"Fredoka", sans-serif', fontSize: "12px", letterSpacing: "2.4px", fontWeight: 800, opacity: 0.92 }}>{name.toUpperCase()}</div>

                      <div style={{ textAlign: "center", marginTop: "2mm", lineHeight: 0.9 }}>
                        <div style={{ fontFamily: '"Luckiest Guy", cursive', fontSize: "22mm", color: "#fde047", textShadow: "0 4px 0 #7c2d12, 0 10px 18px rgba(0,0,0,0.25)" }}>{title}</div>
                        <div style={{ fontFamily: '"Pacifico", cursive', fontSize: "14mm", color: "#fff", marginTop: "-3mm", textShadow: "0 3px 0 #be185d" }}>{winWord}</div>
                      </div>

                      <div style={{ fontFamily: '"Bungee", cursive', fontSize: "7mm", textAlign: "center", marginTop: "2mm", lineHeight: 1.15, maxWidth: "176mm", color: "#fff7ed" }}>
                        {wheel.headline || "Spin To Win"}
                      </div>
                      <div style={{ fontFamily: '"Fredoka", sans-serif', fontSize: "4.2mm", textAlign: "center", marginTop: "2mm", maxWidth: "168mm", lineHeight: 1.35, fontWeight: 600 }}>
                        {description}
                      </div>

                      <div style={{ marginTop: "4mm" }}>
                        <PrizeWheel prizes={prizes} />
                      </div>

                      <div style={{
                        marginTop: "4mm",
                        background: "#111",
                        color: "#fde047",
                        borderRadius: "999px",
                        padding: "2.6mm 9mm",
                        fontFamily: '"Fredoka", sans-serif',
                        fontSize: "5.4mm",
                        fontWeight: 800,
                        letterSpacing: "0.3px",
                        textAlign: "center",
                      }}>
                        {askText}
                      </div>
                    </div>

                    <div style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: "20mm",
                      background: "#0a0a0a",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      fontSize: "16px",
                      fontWeight: 800,
                      zIndex: 4,
                    }}>
                      Powered by
                      <img src={logoSrc} alt="Cashi" style={{ height: "9mm", width: "auto" }} />
                    </div>
                  </div>
                </Box>
              </Box>

              <Box sx={{ display: "grid", gap: 1.4, alignContent: "start" }}>
                <Box sx={{ fontSize: 13, color: consoleColors.muted }}>Prints as one A4 page for people in the store. The spin screen stays on the other device.</Box>
                <TextField size="small" label="Spin word" value={title} onChange={(event) => setTitle(event.target.value)} />
                <TextField size="small" label="Win word" value={winWord} onChange={(event) => setWinWord(event.target.value)} />
                <TextField size="small" label="Description" value={description} onChange={(event) => setDescription(event.target.value)} multiline minRows={3} />
                <TextField size="small" label="Ask text" value={askText} onChange={(event) => setAskText(event.target.value)} />
                <Button variant="contained" sx={primaryButtonSx} onClick={handlePrint} startIcon={<PrintOutlined />}>Print A4</Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
